import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useGetCheckoutSessionQuery,
  useProvisionPaymentSessionMutation,
} from '@backrs_ui/grow-ui/src/redux/subscription/subscriptionApi';

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  SxProps,
  Theme,
} from '@mui/material';

const STRINGS = appStrings.modules.subscription.returnPage;

interface SubscriptionReturnPageProps {
  sessionId?: string;
  onSuccess?: () => void;
  redirectTo?: string;
  isEmbedded?: boolean;
}

export const SubscriptionReturnPage = ({
  sessionId: propSessionId,
  onSuccess,
  redirectTo,
  isEmbedded = false,
}: SubscriptionReturnPageProps = {}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'complete'>('loading');

  // prop session id if coming from onboarding, url params for other cases ( i.e. in-app paywall )
  const sessionId = propSessionId || searchParams.get('session_id');

  // Use the API query to fetch session details - only after successful provision
  const {
    data: sessionData,
    isLoading: sessionLoading,
    error: sessionError,
  } = useGetCheckoutSessionQuery(sessionId || '', {
    skip: !sessionId || status !== 'complete', // Only fetch session data after successful provision
  });

  // Provision payment session mutation
  const [provisionPaymentSession, { data: provisionData, error: provisionError }] =
    useProvisionPaymentSessionMutation();

  // Handle provision response
  useEffect(() => {
    if (provisionData) {
      if (provisionData.provision_status === 'success') {
        setStatus('complete');
      } else {
        setStatus('error');
      }
    }
  }, [provisionData]);

  // Handle provision error
  useEffect(() => {
    if (provisionError) {
      console.error('Provision error:', provisionError);
      setStatus('error');
    }
  }, [provisionError]);

  // Handle session data fetch error
  useEffect(() => {
    if (status === 'complete' && sessionError) {
      console.error('Error fetching session details:', sessionError);
      setStatus('error');
    }
  }, [status, sessionError]);

  // Main effect - call provision endpoint when we have a session ID
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');

      return;
    }

    if (sessionId && status === 'loading') {
      provisionPaymentSession({ sessionId })
        .unwrap()
        .catch((error) => {
          console.error('Provision endpoint error:', error);
          setStatus('error');
        });
    }
  }, [sessionId, status, provisionPaymentSession]);

  const handleGoToDashboard = () => {
    if (onSuccess) {
      // If embedded, call the success callback instead of navigating
      onSuccess();
    } else if (redirectTo) {
      // If standalone, navigate to redirect URL
      navigate(redirectTo);
    } else {
      navigate('/');
    }
  };

  const handleTryAgain = () => {
    navigate('/subscription');
  };

  if (status === 'loading') {
    return (
      <Box sx={isEmbedded ? embeddedContainerSx : containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={loadingContainerSx}>
            <CircularProgress size={24} />
            <Typography variant="text-md-bold">{STRINGS.loading}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // Show loading while fetching session data after successful provision
  if (status === 'complete' && sessionLoading) {
    return (
      <Box sx={isEmbedded ? embeddedContainerSx : containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={loadingContainerSx}>
            <CircularProgress size={24} />
            <Typography variant="text-md-bold">Loading subscription details...</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box sx={isEmbedded ? embeddedContainerSx : containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={{ mb: ({ backrs }) => backrs.spacing['spacing-3xl'] }}>
            <Typography variant="text-lg-semibold">{STRINGS.error.title}</Typography>
            <Typography variant="text-md-medium">{STRINGS.error.subtitle}</Typography>
          </Box>

          <Box sx={buttonContainerSx}>
            <Button
              id="subscription-return-try-again-button"
              hierarchy="Primary"
              size="lg"
              onClick={handleTryAgain}
            >
              {STRINGS.error.tryAgain}
            </Button>
            <Button
              id="subscription-return-contact-support-button"
              hierarchy="Secondary gray"
              size="lg"
              onClick={() => window.open('mailto:support@backrs.com', '_blank')}
            >
              {STRINGS.error.contactSupport}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Success state
  return (
    <Box sx={isEmbedded ? embeddedContainerSx : containerSx}>
      <Box sx={contentWrapperSx}>
        <Box sx={successTitleContainerSx}>
          <Box sx={successsTitleSx}>
            <Typography variant="text-lg-semibold">{STRINGS.success.title}</Typography>
            <Typography variant="text-md-medium">{STRINGS.success.subtitle}</Typography>
          </Box>
          <List disablePadding sx={listSx}>
            {STRINGS.success.features.map((feature, index) => (
              <ListItem key={index} sx={listItemSx}>
                <AppIcon iconName="Check" diameter="20px" color="Brand/500" />
                <Typography variant="text-md-regular">{feature}</Typography>
              </ListItem>
            ))}
          </List>
          <Box sx={emailInfoContainerSx}>
            {sessionData?.customerEmail && (
              <Typography
                variant="text-md-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
              >
                {STRINGS.success.confirmationEmail(sessionData.customerEmail)}
              </Typography>
            )}
            {sessionData?.amountTotal && sessionData?.currency ? (
              <Typography
                variant="text-md-semibold"
                color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
              >
                {STRINGS.success.amountPaid(
                  (sessionData.amountTotal / 100).toFixed(2),
                  sessionData.currency.toUpperCase(),
                )}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Button
          id="subscription-return-go-to-dashboard-button"
          size="lg"
          hierarchy="Primary"
          onClick={handleGoToDashboard}
          sx={buttonSx}
        >
          {onSuccess
            ? STRINGS.success.completeYourOnboarding
            : redirectTo
              ? STRINGS.success.completeYourOnboarding
              : STRINGS.success.goToDashboard}
        </Button>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  height: '100vh',
  width: '100vw',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const embeddedContainerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const contentWrapperSx: SxProps<Theme> = {
  maxWidth: '500px',
  width: '100%',
  textAlign: 'center',
};

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  py: backrs.spacing['spacing-lg'],
});

const successTitleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  alignContent: 'center',
  justifyContent: 'center',
  mb: backrs.spacing['spacing-3xl'],
});

const successsTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const listSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const listItemSx: SxProps<Theme> = ({ backrs }) => ({
  padding: 0,
  margin: 0,
  display: 'flex',
  gap: backrs.spacing['spacing-xl'],
  alignItems: 'center',
});

const emailInfoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});
